import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { ROUTES } from '../../../../utils/constants';
import { calculateDuration, calculateEstimatedPrice } from '../utils/checkoutHelpers';

export const useCheckoutData = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, authCheckComplete } = useAuth();

  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceRates, setPriceRates] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [gstRate] = useState(0.05);

  // Extract booking data from URL params
  const bookingData = useMemo(() => {
    const params = Object.fromEntries(searchParams.entries());

    return {
      bikeId: params.bikeId || '',
      bikeName: params.bikeName || 'Unknown Bike',
      bikeImage: params.bikeImage || '',
      registrationNumber: params.registrationNumber || '',
      storeName: params.storeName || '',
      categoryId: params.categoryId || '',
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      pickupTime: params.pickupTime || '10:00',
      dropoffTime: params.dropoffTime || '19:00',
      city: params.city || '',
      pickupMode: params.pickupMode || 'self-pickup',
      deliveryAddress: params.deliveryAddress || '',
      totalHours: parseFloat(params.totalHours) || 0,
      bookingType: params.bookingType || 'HOURLY',
      packageId: params.packageId || '',
      packageName: params.packageName || '',
      packagePrice: parseFloat(params.packagePrice) || 0,
      hasPackageSelected: params.hasPackageSelected === 'true',
      timestamp: params.timestamp || '',
      source: params.source || ''
    };
  }, [searchParams]);

  // Calculate duration information
  const durationInfo = useMemo(() => {
    return calculateDuration(bookingData);
  }, [bookingData]);

  // Validate booking has required data
  const isValidBooking = useMemo(() => {
    const required = ['bikeId', 'bikeName', 'startDate', 'endDate', 'categoryId'];
    const isValid = required.every(field => bookingData[field]) && durationInfo !== null;

    if (!isValid) {
      console.warn('Invalid booking data:', { bookingData, durationInfo });
    }

    return isValid;
  }, [bookingData, durationInfo]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (authCheckComplete && !isAuthenticated) {
      console.warn('User not authenticated, redirecting to login');
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      navigate(`${ROUTES.LOGIN}?returnUrl=${returnUrl}`);
    }
  }, [isAuthenticated, authCheckComplete, navigate, location]);

  // Fetch price rates for the vehicle category
  useEffect(() => {
    const fetchPriceRates = async () => {
      if (!bookingData.categoryId) {
        console.warn('No category ID available');
        return;
      }

      try {
        console.log('Fetching price rates for category:', bookingData.categoryId);

        const response = await fetch(
          `${import.meta.env.VITE_BASE_URL}/api/prices/category/${bookingData.categoryId}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseData = await response.json();
        const priceList = responseData.success ? responseData.data : responseData;

        if (!Array.isArray(priceList) || priceList.length === 0) {
          throw new Error('No pricing data available');
        }

        let hourlyRate = 0;
        let dailyRate = 0;

        priceList.forEach(price => {
          if (price.days === 0) {
            hourlyRate = price.price;
          } else if (price.days === 1) {
            dailyRate = price.price;
          }
        });

        if (hourlyRate === 0 && dailyRate === 0) {
          throw new Error('No valid rates found in pricing data');
        }

        console.log('✅ Price rates fetched:', { hourlyRate, dailyRate });

        setPriceRates({ hourlyRate, dailyRate });

      } catch (error) {
        console.error('Failed to fetch price rates:', error);
        setPriceRates({ hourlyRate: 100, dailyRate: 1000 });
      }
    };

    if (bookingData.categoryId) {
      fetchPriceRates();
    }
  }, [bookingData.categoryId]);

  // Calculate estimated price
  useEffect(() => {
    if (!isValidBooking || !durationInfo) {
      console.log("Skipping price calculation - invalid booking or duration");
      return;
    }

    if (!priceRates && !bookingData.hasPackageSelected) {
      console.log("Waiting for price rates...");
      return;
    }

    setPriceLoading(true);

    const price = calculateEstimatedPrice(bookingData, durationInfo, priceRates);
    setEstimatedPrice(price);
    setPriceLoading(false);
  }, [bookingData, durationInfo, isValidBooking, priceRates]);

  // Calculate final totals with GST on discounted amount
  // Security deposit is no longer collected — payableAmount === total.
  const totals = useMemo(() => {
    if (!estimatedPrice) {
      return {
        subtotal: 0,
        couponDiscount: 0,
        discountedSubtotal: 0,
        gst: 0,
        deposit: 0,
        payableAmount: 0,
        total: 0
      };
    }

    const subtotal = Math.round(estimatedPrice.subtotal || 0);
    const discount = Math.round(couponDiscount || 0);

    const discountedSubtotal = Math.max(0, subtotal - discount);
    const gst = Math.round(discountedSubtotal * gstRate);

    const payableAmount = discountedSubtotal + gst;

    return {
      subtotal,
      couponDiscount: discount,
      discountedSubtotal,
      gst,
      deposit: 0,
      payableAmount,
      total: payableAmount
    };
  }, [estimatedPrice, couponDiscount, gstRate]);

  return {
    bookingData,
    durationInfo,
    isValidBooking,
    estimatedPrice,
    priceLoading,
    priceRates,
    totals,
    couponDiscount,
    setCouponDiscount,
    authCheckComplete
  };
};
